import React, { useState } from 'react';
import { fetchWithRetry } from '../utils/newsletterManagement';
import '../styles/AdaChecker.css';

const RULES = [
  {
    id: 'image-alt',
    selector: 'img',
    test: (el) => !el.hasAttribute('alt') || el.getAttribute('alt').trim() === '',
    message: 'Image missing descriptive alt text'
  },
  {
    id: 'heading-empty',
    selector: 'h1,h2,h3,h4,h5,h6',
    test: (el) => el.textContent.trim() === '',
    message: 'Heading element is empty'
  },
  {
    id: 'link-name',
    selector: 'a',
    test: (el) => el.textContent.trim() === '' && !el.getAttribute('aria-label'),
    message: 'Link has no visible text or aria-label'
  },
  {
    id: 'button-name',
    selector: 'button,input[type="button"],input[type="submit"]',
    test: (el) => {
      const text = (el.textContent || el.value || '').trim();
      return text === '' && !el.getAttribute('aria-label');
    },
    message: 'Button has no visible text or aria-label'
  },
  {
    id: 'form-label',
    selector: 'input,textarea,select',
    test: (el, doc) => {
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.getAttribute('aria-label')) return false;
      if (el.id && doc.querySelector(`label[for="${el.id}"]`)) return false;
      // implicit label via parent <label>
      if (el.closest('label')) return false;
      return true;
    },
    message: 'Form control missing label or aria-label'
  }
];

const generateSelector = (el) => {
  try {
    const parts = [];
    while (el && el.nodeType === 1 && el.tagName.toLowerCase() !== 'html') {
      let part = el.tagName.toLowerCase();
      if (el.id) {
        part += `#${el.id}`;
        parts.unshift(part);
        break;
      }
      const cls = el.className && typeof el.className === 'string' ? el.className.split(' ').filter(Boolean)[0] : null;
      if (cls) part += `.${cls}`;
      let sib = el;
      let nth = 1;
      while (sib = sib.previousElementSibling) {
        if (sib.tagName === el.tagName) nth++;
      }
      if (nth > 1) part += `:nth-of-type(${nth})`;
      parts.unshift(part);
      el = el.parentElement;
    }
    return parts.join(' > ');
  } catch {
    return '';
  }
};

const AdaChecker = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const validateUrl = (value) => {
    try {
      const u = new URL(value);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const analyzeHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const violations = [];

    RULES.forEach(rule => {
      const els = Array.from(doc.querySelectorAll(rule.selector));
      els.forEach(el => {
        const failed = rule.test.length === 2 ? rule.test(el, doc) : rule.test(el);
        if (failed) {
          violations.push({
            id: rule.id,
            message: rule.message,
            selector: generateSelector(el),
            html: el.outerHTML
          });
        }
      });
    });

    return {
      timestamp: new Date().toISOString(),
      url,
      totalIssues: violations.length,
      violations
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setReport(null);

    if (!validateUrl(url)) {
      setError('Enter a valid URL starting with http:// or https://');
      return;
    }

    setLoading(true);
    try {
      // attempt to fetch the page HTML
      const resp = await fetchWithRetry(url, {
        headers: { 'Accept': 'text/html' }
      });

      if (!resp.ok) {
        // provide clearer guidance on CORS or remote failures
        throw new Error(`Failed to fetch page: ${resp.status} ${resp.statusText}`);
      }

      const html = await resp.text();
      const result = analyzeHtml(html);
      setReport(result);
    } catch (err) {
      // Detect common CORS failure patterns
      const msg = (err && err.message) ? err.message : String(err);
      if (msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('networkrequest')) {
        setError('Failed to analyze page: network/CORS error. For cross-origin sites you must run analysis from a server-side proxy or enable CORS for the target domain.');
      } else {
        setError(`Failed to analyze page: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ada-checker-component">
      <form onSubmit={handleSubmit} className="ada-form">
        <input
          type="url"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          aria-label="Website URL"
          className="ada-url-input"
        />
        <button type="submit" disabled={loading} className="ada-check-button">
          {loading ? 'Analyzing…' : 'Check Compliance'}
        </button>
      </form>

      {error && <div className="ada-error">Error: {error}</div>}

      {report && (
        <div className="ada-report">
          <h3>Accessibility Report</h3>
          <p>URL: {report.url}</p>
          <p>Checked at: {report.timestamp}</p>
          <p>Total issues: {report.totalIssues}</p>

          {report.violations.length > 0 ? (
            <div className="ada-violations">
              {report.violations.map((v, i) => (
                <div key={i} className="ada-violation">
                  <strong>{v.message}</strong>
                  <div className="ada-selector">{v.selector}</div>
                  <pre className="ada-html">{v.html}</pre>
                </div>
              ))}
            </div>
          ) : (
            <div className="ada-no-issues">No issues found by the built-in checks.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdaChecker;
